// =============================================================================
// SimSense Platform — User Profile Page (OT-245: Enhanced public profile)
// =============================================================================

import { type ChangeEvent, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../utils/api";
import { useAuthStore } from "../state/authStore";
import { useToast } from "../components/Toast";
import { SessionCard } from "../components/SessionCard";
import "./Profile.css";

export function Profile() {
  const { id } = useParams<{ id: string }>();
  const { user: currentUser, updateProfile, uploadAvatar } = useAuthStore();
  const toast = useToast();
  const [profile, setProfile] = useState<any>(null);
  const [sessions, setSessions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [editForm, setEditForm] = useState({
    displayName: "",
    bio: "",
    location: "",
    iracingId: "",
    publicProfile: true,
    twitchUrl: "",
    youtubeUrl: "",
    twitterUrl: "",
    discordUsername: "",
  });

  const isMe = currentUser?.userId === id || id === "me";
  const profileId = id === "me" ? currentUser?.userId : id;

  useEffect(() => {
    document.title = profile?.displayName
      ? `${profile.displayName} | SimSense`
      : "Profile | SimSense";
  }, [profile]);

  useEffect(() => {
    async function fetchProfile() {
      if (!profileId) return;
      setLoading(true);
      setError(null);
      try {
        const [pData, sData] = await Promise.all([
          api<any>(`/api/profiles/${profileId}`),
          api<any[]>(`/api/profiles/${profileId}/sessions`)
        ]);
        setProfile(pData);
        setSessions(sData || []);
      } catch (err: any) {
        console.error("Failed to fetch profile", err);
        setError(err.message || "Failed to load profile. This user may have a private profile.");
      } finally {
        setLoading(false);
      }
    }
    fetchProfile();
  }, [profileId]);

  const startEditing = () => {
    setEditForm({
      displayName: profile?.displayName || "",
      bio: profile?.bio || "",
      location: profile?.location || "",
      iracingId: profile?.iracingId || "",
      publicProfile: profile?.publicProfile ?? true,
      twitchUrl: profile?.twitchUrl || "",
      youtubeUrl: profile?.youtubeUrl || "",
      twitterUrl: profile?.twitterUrl || "",
      discordUsername: profile?.discordUsername || "",
    });
    setEditing(true);
  };

  const handleSaveProfile = async () => {
    try {
      await updateProfile({
        display_name: editForm.displayName,
        bio: editForm.bio,
        location: editForm.location,
        iracing_id: editForm.iracingId,
        public_profile: editForm.publicProfile,
        twitch_url: editForm.twitchUrl,
        youtube_url: editForm.youtubeUrl,
        twitter_url: editForm.twitterUrl,
        discord_username: editForm.discordUsername,
      });
      setProfile({ ...profile, ...editForm });
      setEditing(false);
      toast.success("Profile updated.");
    } catch {
      toast.error("Failed to update profile.");
    }
  };

  const handleAvatarSelected = async (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const file = input.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Select a PNG, JPEG, WebP, or GIF image.");
      input.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Avatar must be under 5MB.");
      input.value = "";
      return;
    }

    setAvatarUploading(true);
    try {
      const updatedUser = await uploadAvatar(file);
      setProfile((current: any) => current ? {
        ...current,
        profilePictureUrl: updatedUser.profilePictureUrl,
      } : current);
      toast.success("Avatar updated.");
    } catch (err: any) {
      toast.error(err?.message || "Failed to upload avatar.");
    } finally {
      setAvatarUploading(false);
      input.value = "";
    }
  };

  if (loading) {
    return (
      <div className="profile-loading">
        <div className="profile-loading-card">Loading profile...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="profile-error">
        <div className="profile-error-card">
          <div className="profile-error-icon">🔒</div>
          <h3>Profile Unavailable</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!profile) return null;

  const initials = (profile.displayName || "U").substring(0, 1).toUpperCase();
  const tierLabel = profile.plan || profile.tier || "free";
  const tierColor = tierLabel === "admin" ? "var(--bad)" : tierLabel === "pro" ? "var(--accent)" : "var(--text3)";

  const uniqueTracks = new Set(sessions.map(s => s.trackName).filter(Boolean)).size;
  const uniqueCars = new Set(sessions.map(s => s.carName).filter(Boolean)).size;

  return (
    <div className="profile-page">
      {/* Profile Header */}
      <div className="profile-header">
        <div className="profile-banner" />

        <div className="profile-body">
          <div className="profile-top-row">
            <div className="profile-identity">
              {/* OT-246: Clickable avatar — click to change profile picture (own profile only) */}
              <div
                className={`profile-avatar-wrapper${isMe ? " is-clickable" : ""}`}
                onClick={() => {
                  if (!isMe || avatarUploading) return;
                  document.getElementById("profile-avatar-input")?.click();
                }}
                title={isMe ? "Click to change avatar" : undefined}
              >
                {profile.profilePictureUrl ? (
                  <img
                    src={profile.profilePictureUrl}
                    alt={profile.displayName}
                    className="profile-avatar-img"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; (e.target as HTMLImageElement).nextElementSibling?.classList.remove("profile-avatar-fallback--hidden"); }}
                  />
                ) : null}
                <div className={`profile-avatar-fallback${profile.profilePictureUrl ? " profile-avatar-fallback--hidden" : ""}`}>{initials}</div>
                {isMe && (
                  <div className="profile-avatar-overlay">
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                      <circle cx="12" cy="13" r="4" />
                    </svg>
                  </div>
                )}
                <input
                  id="profile-avatar-input"
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  style={{ display: "none" }}
                  onChange={handleAvatarSelected}
                  disabled={avatarUploading}
                />
              </div>
              <div className="profile-name-block">
                <div className="profile-name-row">
                  <h2 className="profile-name">{profile.displayName || "Anonymous"}</h2>
                  <span
                    className="profile-tier-badge"
                    style={{
                      background: `color-mix(in srgb, ${tierColor} 15%, transparent)`,
                      color: tierColor,
                      border: `1px solid color-mix(in srgb, ${tierColor} 30%, transparent)`,
                    }}
                  >
                    {tierLabel}
                  </span>
                </div>
                {profile.location && (
                  <div className="profile-location">{profile.location}</div>
                )}
              </div>
            </div>

            {isMe && !editing && (
              <button className="profile-edit-btn" onClick={startEditing}>
                Edit Profile
              </button>
            )}
          </div>

          {/* Bio */}
          {!editing && (profile.bio ? (
            <p className="profile-bio">{profile.bio}</p>
          ) : isMe ? (
            <p className="profile-bio-empty">
              No bio yet. <button onClick={startEditing}>Add one</button>
            </p>
          ) : null)}

          {/* Social Links */}
          {!editing && <SocialLinks profile={profile} />}

          {/* Inline Edit Form */}
          {editing && isMe && (
            <div className="profile-edit-form">
              {avatarUploading && (
                <div style={{ fontSize: "12px", color: "var(--accent)", marginBottom: "8px" }}>Uploading avatar...</div>
              )}
              <div>
                <label className="profile-edit-label">Display Name</label>
                <input
                  type="text"
                  className="profile-edit-input"
                  value={editForm.displayName}
                  onChange={e => setEditForm({ ...editForm, displayName: e.target.value })}
                />
              </div>
              <div>
                <label className="profile-edit-label">iRacing Customer ID</label>
                <input
                  type="text"
                  className="profile-edit-input"
                  value={editForm.iracingId}
                  onChange={e => setEditForm({ ...editForm, iracingId: e.target.value })}
                  placeholder="e.g. 123456"
                />
              </div>
              <div>
                <label className="profile-edit-label">Location</label>
                <input
                  type="text"
                  className="profile-edit-input"
                  value={editForm.location}
                  onChange={e => setEditForm({ ...editForm, location: e.target.value })}
                  placeholder="e.g. Charlotte, NC"
                />
              </div>
              <div>
                <label className="profile-edit-label">Bio</label>
                <textarea
                  className="profile-edit-textarea"
                  value={editForm.bio}
                  onChange={e => setEditForm({ ...editForm, bio: e.target.value })}
                  placeholder="Tell the community about yourself..."
                />
              </div>
              <div className="profile-social-edit-section">
                <label className="profile-edit-label">Social Links</label>
                <div className="profile-social-edit-grid">
                  <div className="profile-social-edit-row">
                    <span className="profile-social-edit-icon">{SOCIAL_ICONS.twitch}</span>
                    <input
                      type="url"
                      className="profile-edit-input"
                      value={editForm.twitchUrl}
                      onChange={e => setEditForm({ ...editForm, twitchUrl: e.target.value })}
                      placeholder="https://twitch.tv/username"
                    />
                  </div>
                  <div className="profile-social-edit-row">
                    <span className="profile-social-edit-icon">{SOCIAL_ICONS.youtube}</span>
                    <input
                      type="url"
                      className="profile-edit-input"
                      value={editForm.youtubeUrl}
                      onChange={e => setEditForm({ ...editForm, youtubeUrl: e.target.value })}
                      placeholder="https://youtube.com/@channel"
                    />
                  </div>
                  <div className="profile-social-edit-row">
                    <span className="profile-social-edit-icon">{SOCIAL_ICONS.twitter}</span>
                    <input
                      type="url"
                      className="profile-edit-input"
                      value={editForm.twitterUrl}
                      onChange={e => setEditForm({ ...editForm, twitterUrl: e.target.value })}
                      placeholder="https://x.com/username"
                    />
                  </div>
                  <div className="profile-social-edit-row">
                    <span className="profile-social-edit-icon">{SOCIAL_ICONS.discord}</span>
                    <input
                      type="text"
                      className="profile-edit-input"
                      value={editForm.discordUsername}
                      onChange={e => setEditForm({ ...editForm, discordUsername: e.target.value })}
                      placeholder="username#1234"
                    />
                  </div>
                </div>
              </div>
              <div className="profile-checkbox-row">
                <input
                  type="checkbox"
                  id="editPublicProfile"
                  checked={editForm.publicProfile}
                  onChange={e => setEditForm({ ...editForm, publicProfile: e.target.checked })}
                />
                <label htmlFor="editPublicProfile">Make my profile public</label>
              </div>
              <div className="profile-edit-actions">
                <button className="profile-save-btn" onClick={handleSaveProfile}>Save</button>
                <button className="profile-cancel-btn" onClick={() => setEditing(false)}>Cancel</button>
              </div>
            </div>
          )}

          {/* Stats Bar */}
          <div className="profile-stats">
            <StatBlock label="Sessions" value={profile.sessionCount} />
            <StatBlock label="Tracks" value={uniqueTracks} />
            <StatBlock label="Cars" value={uniqueCars} />
            <StatBlock label="Teams" value={profile.teamCount} />
            <StatBlock label="Member Since" value={new Date(profile.createdAt).getFullYear()} />
          </div>
        </div>
      </div>

      {/* Recent Sessions */}
      <div>
        <h3 className="profile-sessions-title">
          {isMe ? "Your Recent Sessions" : "Recent Public Sessions"}
        </h3>

        {sessions.length === 0 ? (
          <div className="profile-sessions-empty">
            <div className="profile-sessions-empty-icon">🏁</div>
            <p>
              {isMe ? "No sessions yet. Upload an .ibt file to get started." : "No public sessions found."}
            </p>
          </div>
        ) : (
          <div className="profile-sessions-grid">
            {sessions.map(s => (
              <SessionCard key={s.id} session={s} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatBlock({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <div className="stat-block-label">{label}</div>
      <div className="stat-block-value">{value}</div>
    </div>
  );
}

/* Social link icons (inline SVG for zero-dependency rendering) */
const SOCIAL_ICONS = {
  twitch: (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
      <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z"/>
    </svg>
  ),
  youtube: (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12z"/>
    </svg>
  ),
  twitter: (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </svg>
  ),
  discord: (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
    </svg>
  ),
};

function SocialLinks({ profile }: { profile: any }) {
  const links = [
    { key: "twitchUrl", icon: SOCIAL_ICONS.twitch, label: "Twitch", color: "#9146ff" },
    { key: "youtubeUrl", icon: SOCIAL_ICONS.youtube, label: "YouTube", color: "#ff0000" },
    { key: "twitterUrl", icon: SOCIAL_ICONS.twitter, label: "X", color: "#fff" },
    { key: "discordUsername", icon: SOCIAL_ICONS.discord, label: "Discord", color: "#5865f2" },
  ];

  const activeLinks = links.filter(l => profile[l.key]);
  if (activeLinks.length === 0) return null;

  return (
    <div className="profile-social-links">
      {activeLinks.map(l => {
        const value = profile[l.key];
        const isUrl = value.startsWith("http");
        return isUrl ? (
          <a
            key={l.key}
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="profile-social-link"
            style={{ color: l.color }}
            title={l.label}
          >
            {l.icon}
            <span>{l.label}</span>
          </a>
        ) : (
          <span
            key={l.key}
            className="profile-social-link"
            style={{ color: l.color }}
            title={`${l.label}: ${value}`}
          >
            {l.icon}
            <span>{value}</span>
          </span>
        );
      })}
    </div>
  );
}
